import { ApiError, erpApi } from "../api/client";
import type { CommandRequest } from "./types";

type Request = Omit<CommandRequest, "commandId">;
type Entry = { request: Request; commandId: string; serverId?: string };
const volatile = new Map<string, Entry[]>();
function key(actor: string) { return `rm-pending-operations:${actor}`; }
function read(actor: string): Entry[] {
  if (volatile.has(actor)) return volatile.get(actor)!;
  try { return JSON.parse(sessionStorage.getItem(key(actor)) ?? "[]") as Entry[]; }
  catch { return volatile.get(actor) ?? []; }
}
function write(actor: string, values: Entry[]): boolean {
  try { sessionStorage.setItem(key(actor), JSON.stringify(values)); volatile.delete(actor); return true; }
  catch { volatile.set(actor, values); return false; }
}
export function pendingOperationalCommands(actor: string) { return read(actor).map(entry => entry.request); }
export async function submitOperationalCommand(request: Request, actor: string, options: { attempts?: number; wait?: () => Promise<void> } = {}) {
  const list = read(actor);
  const previous = list.find(entry => entry.request.type === request.type && entry.request.aggregateId === request.aggregateId);
  if (previous && JSON.stringify(previous.request.payload) !== JSON.stringify(request.payload)) throw new Error("Existe una operación pendiente para este registro. Consúltala antes de enviar otro cambio.");
  const entry: Entry = previous ?? { request, commandId: crypto.randomUUID() };
  if (!previous) list.push(entry);
  // Persist the retry key before dispatch; otherwise a reload could duplicate the operation.
  if (!entry.serverId && !write(actor, list)) throw new Error("No pudimos guardar la operación en este dispositivo. Libera espacio del navegador y reintenta; aún no se envió ningún cambio.");
  const remove = () => write(actor, read(actor).filter(item => item.commandId !== entry.commandId));
  const pendingMessage = "Pendiente de sincronizar. No se ha confirmado el cambio. Puedes consultar de nuevo la misma operación sin duplicarla.";
  try {
    if (!entry.serverId) {
      const queued = await erpApi.command({ ...entry.request, commandId: entry.commandId });
      entry.serverId = queued.command_id;
      write(actor, read(actor).map(item => item.commandId === entry.commandId ? entry : item));
    }
    for (let attempt = 0; attempt < (options.attempts ?? 5); attempt++) {
      const result = await erpApi.commandStatus(entry.serverId);
      if (["completed", "applied", "duplicate"].includes(result.status) || result.status === "accepted" && !!result.completed_at) { remove(); return; }
      if (["failed", "rejected", "version_conflict", "conflict", "blocked_read_only", "cooldown"].includes(result.status)) {
        remove();
        throw new ApiError(result.response_payload?.message ?? result.last_error ?? (result.status.includes("conflict") ? "La reserva cambió. Actualiza sus datos antes de intentarlo de nuevo." : "WordPress rechazó la operación. Revisa el estado del registro."), 409, result.status);
      }
      if (attempt + 1 < (options.attempts ?? 5)) await (options.wait?.() ?? new Promise<void>(resolve => window.setTimeout(resolve, 750)));
    }
    throw new Error(pendingMessage);
  } catch (caught) {
    if (caught instanceof ApiError && caught.status >= 400 && caught.status < 500) {
      // Authentication/rate-limit failures while checking an accepted command do not cancel it.
      // STALE_VERSION on POST proves this key was not queued: the server replays
      // an existing receipt before validating the aggregate version.
      if (!entry.serverId && (!previous || caught.status === 409 && caught.code === "STALE_VERSION")) remove();
      throw caught;
    }
    throw new Error(pendingMessage);
  }
}
